import React, { useState, useEffect, useRef } from "react";
import { 
  Plus, X, Edit2, Trash2, Upload, Image as ImageIcon, 
  Calendar, ArrowLeft, Award, Check, Loader
} from "lucide-react";
import Swal from "sweetalert2";
import axios from 'axios';

const TradeAwards = () => {
  const [viewMode, setViewMode] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);

  if (viewMode === 'categories') {
    return <CategoriesView onSelectCategory={(cat) => {
      setSelectedCategory(cat);
      setViewMode('categoryDetail');
    }} />;
  }

  return <CategoryDetailView 
    category={selectedCategory} 
    onBack={() => setViewMode('categories')} 
  />;
};

const CategoriesView = ({ onSelectCategory }) => {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    year: new Date().getFullYear().toString()
  });

  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com/api/dla-trade",
    headers: { "Content-Type": "application/json" },
    timeout: 10000,
  });

  const API_URL = "/trade-awards/categories/";

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_URL);
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Unable to load categories from server. Please check if the backend is running."
      });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Name", text: "Please enter a category name." });
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: form.name.trim(),
        year: form.year
      };

      if (editingCategory?.id) {
        await api.put(`${API_URL}${editingCategory.id}/`, data);
        Swal.fire({ icon: "success", title: "Category Updated!", timer: 1500 });
      } else {
        await api.post(API_URL, data);
        Swal.fire({ icon: "success", title: "Category Created!", timer: 1500 });
      }

      closeModal();
      await loadCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      let errorMessage = "Failed to save category. Please try again.";
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          errorMessage = Object.values(error.response.data).flat().join(', ');
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This award category and all its images will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`${API_URL}${id}/`);
      const updated = categories.filter(cat => cat.id !== id);
      setCategories(updated);
      Swal.fire({ icon: "success", title: "Category Deleted!", timer: 1500 });
    } catch (error) {
      console.error("Error deleting category:", error);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error.response?.data?.detail || "Failed to delete category. Please try again."
      });
    }
  };

  const openModal = (category = null) => {
    setEditingCategory(category);
    setForm({
      name: category?.name || "",
      year: category?.year || new Date().getFullYear().toString()
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setForm({ name: "", year: new Date().getFullYear().toString() });
  };

  const sortedCategories = [...categories].sort((a, b) => b.year - a.year);

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">DLA Trade Awards</h1>
            <p className="text-gray-600 mt-1">Manage award categories and images</p>
          </div>
          <button
            onClick={() => openModal()}
            className="flex items-center px-6 py-2.5 rounded-lg bg-emerald-600 text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            <Plus className="w-5 h-5 mr-2" /> Add Award Category
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {sortedCategories.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg mb-2">No award categories yet</p>
            <p className="text-gray-400 text-sm">Click "Add Award Category" to create your first award</p>
          </div>
        ) : (
          <div className="space-y-4 animate-fadeIn">
            {sortedCategories.map((category) => (
              <div 
                key={category.id} 
                className="group flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50/50 px-3 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <span 
                      onClick={() => onSelectCategory(category)}
                      className="text-[24px] text-emerald-600 font-normal hover:underline cursor-pointer transition-all"
                    >
                      {category.name} ({category.year})
                    </span>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm text-gray-500">
                        {category.image_count || 0} images
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Edit/Delete Buttons */}
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => openModal(category)}
                    className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-600/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => deleteCategory(category.id)}
                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md pointer-events-auto overflow-hidden animate-modalIn">
              <div className="px-8 py-5 border-b bg-gray-50/50 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingCategory ? "Edit Award Category" : "Add Award Category"}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-8">
                <form onSubmit={saveCategory} className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Award Name *
                    </label>
                    <input 
                      value={form.name} 
                      onChange={e => setForm({...form, name: e.target.value})} 
                      className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-emerald-500/50 transition-all" 
                      placeholder="e.g., DLA Trade Awards" 
                      required 
                      disabled={saving}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Year *
                    </label>
                    <input 
                      type="number"
                      value={form.year} 
                      onChange={e => setForm({...form, year: e.target.value})} 
                      className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-emerald-500/50 transition-all" 
                      min="2000"
                      max="2100"
                      required
                      disabled={saving}
                    />
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <button 
                      type="button" 
                      onClick={closeModal} 
                      className="flex-1 px-5 py-3 border border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-colors" 
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="flex-1 px-5 py-3 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center" 
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : editingCategory ? 'Update Category' : 'Create Category'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
      `}</style>
    </div>
  );
};

const CategoryDetailView = ({ category, onBack }) => {
  const [images, setImages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(false);

  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com/api/dla-trade",
    headers: { "Content-Type": "application/json" },
    timeout: 30000,
  });

  const API_URL = "/trade-awards/images/";

  useEffect(() => {
    if (category?.id) {
      loadImages();
    }
  }, [category?.id]);

  const loadImages = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API_URL}?category_id=${category.id}`);
      if (response.data && Array.isArray(response.data)) {
        setImages(response.data);
      }
    } catch (error) {
      console.error("Error loading images:", error);
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Unable to load images from server."
      });
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This image will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`${API_URL}${id}/`);
      const updated = images.filter(img => img.id !== id);
      setImages(updated);
      Swal.fire({ icon: "success", title: "Image Deleted!", timer: 1500 });
    } catch (error) {
      console.error("Error deleting image:", error);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error.response?.data?.detail || "Failed to delete image. Please try again."
      });
    }
  };

  const openModal = () => {
    setSelectedFiles([]);
    setUploadProgress([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedFiles([]);
    setUploadProgress([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = [];
    const invalidFiles = [];
    
    files.forEach(file => {
      if (file.type.match('image.*') && file.size <= 5 * 1024 * 1024) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });
    
    if (invalidFiles.length > 0) {
      Swal.fire({ 
        icon: "warning", 
        title: "Some Files Skipped", 
        html: `${validFiles.length} valid image(s) selected.<br>${invalidFiles.length} file(s) skipped (invalid type or >5MB).`,
        timer: 3000 
      });
    }
    
    setSelectedFiles(validFiles);
    setUploadProgress([]);
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    
    const newProgress = [...uploadProgress];
    newProgress.splice(index, 1);
    setUploadProgress(newProgress);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      Swal.fire({ icon: "warning", title: "No Images Selected", text: "Please select at least one image to upload." });
      return;
    }

    setUploading(true);
    const progress = Array(selectedFiles.length).fill({ status: 'pending', message: 'Waiting...' });
    setUploadProgress(progress);

    let successCount = 0;
    let errorCount = 0;

    // Upload files sequentially
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      try {
        // Update progress
        setUploadProgress(prev => {
          const newProgress = [...prev];
          newProgress[i] = { status: 'uploading', message: 'Converting to base64...' };
          return newProgress;
        });

        // Convert file to base64
        const base64 = await fileToBase64(file);
        
        setUploadProgress(prev => {
          const newProgress = [...prev];
          newProgress[i] = { status: 'uploading', message: 'Uploading to server...' };
          return newProgress;
        });

        // Upload to server
        const data = {
          category: category.id,
          image_base64: base64
        };

        await api.post(API_URL, data);
        
        setUploadProgress(prev => {
          const newProgress = [...prev];
          newProgress[i] = { status: 'success', message: 'Upload successful!' };
          return newProgress;
        });
        
        successCount++;
        
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        
        setUploadProgress(prev => {
          const newProgress = [...prev];
          newProgress[i] = { status: 'error', message: error.response?.data?.detail || 'Upload failed' };
          return newProgress;
        });
        
        errorCount++;
        // Continue with next file even if one fails
        continue;
      }
    }

    setUploading(false);
    
    // Show results and reload images
    if (successCount > 0) {
      await loadImages();
      
      if (successCount === selectedFiles.length) {
        Swal.fire({ 
          icon: "success", 
          title: "Upload Complete!", 
          html: `Successfully uploaded ${successCount} image(s).`,
          timer: 2000 
        });
        closeModal();
      } else {
        Swal.fire({ 
          icon: "warning", 
          title: "Partial Success", 
          html: `Uploaded ${successCount} of ${selectedFiles.length} images. ${errorCount} failed.`
        });
      }
    } else {
      Swal.fire({ 
        icon: "error", 
        title: "Upload Failed", 
        text: "Failed to upload any images. Please try again." 
      });
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <button onClick={onBack} className="flex items-center gap-2 text-black-600 hover:text-emerald-700 mb-4">
              <ArrowLeft className="w-5 h-5" /> 
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{category.name} ({category.year})</h1>
            <p className="text-gray-600 mt-1">Manage award images</p>
          </div>
          
          <button
            onClick={openModal}
            className="flex items-center px-6 py-2.5 rounded-lg bg-emerald-600 text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            <Plus className="w-5 h-5 mr-2" /> Add Images
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {/* HEADING */}
        <div className="mb-10">
          <h2 className="text-2xl font-medium text-gray-800 mb-6">
            The DLA trade awards for {category.year} is:
          </h2>
        </div>

        {images.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg mb-2">No award images yet</p>
            <p className="text-gray-400 text-sm">Click "Add Images" to upload multiple images at once</p>
          </div>
        ) : (
          <div className="animate-fadeIn">
            {/* SMALLER WIDTH CONTAINERS, BUT SAME IMAGE SIZE */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              {images.map(image => (
                <div 
                  key={image.id} 
                  className="group relative"
                >
                  {/* Container with smaller width only */}
                  <div className="relative rounded-lg border border-gray-200 p-4 bg-white shadow-sm hover:shadow-md transition-all duration-300 max-w-sm mx-auto">
                    {/* BUTTONS ON TOP OF IMAGE */}
                    <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <button 
                        onClick={() => deleteImage(image.id)}
                        className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transform hover:scale-110 transition-transform duration-200"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                    
                    {/* SLIGHTLY BIGGER IMAGE - FREE WIDTH/HEIGHT */}
                    <div className="flex items-center justify-center">
                      <img 
                        src={image.image_url} 
                        alt={`Award for ${category.name} ${category.year}`} 
                        className="w-auto h-auto max-w-full max-h-64 object-contain"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=max&w=400";
                          e.target.alt = "Image not available";
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Total Images Count */}
            <div className="mt-10 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-center text-sm">
                Total {images.length} award {images.length === 1 ? 'image' : 'images'} for {category.year}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal - Single button for multiple images */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl pointer-events-auto overflow-hidden animate-modalIn">
              <div className="px-8 py-5 border-b bg-gray-50/50 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">
                  Add Award Images
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-8 max-h-[70vh] overflow-y-auto">
                <form onSubmit={handleUpload} className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Select Multiple Images *
                    </label>
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed ${selectedFiles.length > 0 ? 'border-emerald-300' : 'border-emerald-300 hover:border-emerald-400'} rounded-2xl p-8 text-center cursor-pointer hover:bg-gray-50/50 transition-all`}
                    >
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileSelect} 
                        accept="image/*" 
                        multiple 
                        className="hidden" 
                        disabled={uploading} 
                      />
                      <Upload className={`w-12 h-12 mx-auto mb-4 ${selectedFiles.length > 0 ? 'text-emerald-500' : 'text-emerald-500'}`} />
                      <p className="text-gray-700 font-medium mb-1">
                        {selectedFiles.length > 0 ? `${selectedFiles.length} image(s) selected` : 'Click to select images'}
                      </p>
                      <p className="text-gray-500 text-sm">Select one or multiple images (Max 5MB each)</p>
                      <p className="text-gray-400 text-xs mt-2">You can select multiple images at once (Ctrl+Click or Shift+Click)</p>
                    </div>
                  </div>

                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-medium text-gray-700">Selected Images ({selectedFiles.length})</h3>
                      <div className="max-h-48 overflow-y-auto border rounded-xl p-3">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg mb-2 last:mb-0">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <img 
                                  src={URL.createObjectURL(file)} 
                                  alt={file.name} 
                                  className="w-12 h-12 object-cover rounded"
                                />
                                {uploadProgress[index]?.status === 'success' && (
                                  <div className="absolute inset-0 bg-emerald-500/20 rounded flex items-center justify-center">
                                    <Check className="w-6 h-6 text-emerald-600" />
                                  </div>
                                )}
                                {uploadProgress[index]?.status === 'error' && (
                                  <div className="absolute inset-0 bg-red-500/20 rounded flex items-center justify-center">
                                    <X className="w-6 h-6 text-red-600" />
                                  </div>
                                )}
                                {uploadProgress[index]?.status === 'uploading' && (
                                  <div className="absolute inset-0 bg-blue-500/20 rounded flex items-center justify-center">
                                    <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                                <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                {uploadProgress[index] && (
                                  <p className={`text-xs mt-1 ${
                                    uploadProgress[index].status === 'success' ? 'text-emerald-600' :
                                    uploadProgress[index].status === 'error' ? 'text-red-600' :
                                    'text-blue-600'
                                  }`}>
                                    {uploadProgress[index].message}
                                  </p>
                                )}
                              </div>
                            </div>
                            {!uploading && (
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                disabled={uploading}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Upload Progress Summary */}
                      {uploading && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-blue-700">Upload Progress</span>
                            <span className="text-sm text-blue-600">
                              {uploadProgress.filter(p => p?.status === 'success').length} / {selectedFiles.length}
                            </span>
                          </div>
                          <div className="w-full bg-blue-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${(uploadProgress.filter(p => p?.status === 'success').length / selectedFiles.length) * 100}%` 
                              }}
                            ></div>
                          </div>
                          <p className="text-xs text-blue-600 mt-2">
                            Please wait while images are being uploaded. Do not close this window.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex gap-4 pt-4">
                    <button 
                      type="button" 
                      onClick={closeModal} 
                      className="flex-1 px-5 py-3 border border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-colors" 
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="flex-1 px-5 py-3 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center" 
                      disabled={uploading || selectedFiles.length === 0}
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Uploading ({uploadProgress.filter(p => p?.status === 'success').length}/{selectedFiles.length})...
                        </>
                      ) : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TradeAwards;